import ReactEChartsCore from 'echarts-for-react/lib/core';
import { ChartContainer } from '../../analytics/shared/ChartContainer';
import { EmptyState } from '../../analytics/shared/EmptyState';
import { echarts } from '../../analytics/shared/echarts-config';
import { buildDonutOptions, buildHorizontalBarOptions } from '../../analytics/shared/chart-option-builders';
import type {
  EngagementMetricsBillableSplit,
  EngagementMetricsTypeBreakdown,
} from '../../../types/engagement-metrics.types';

type EngagementTypeAndBillableChartsProps = {
  byType: EngagementMetricsTypeBreakdown[];
  billableSplit: EngagementMetricsBillableSplit;
};

const BILLABLE_COLOR = '#2f7d5e';
const NON_BILLABLE_COLOR = '#aab2ab';

const buildTypeOptions = (data: EngagementMetricsTypeBreakdown[]) =>
  buildHorizontalBarOptions(
    data.map((item) => ({
      label: item.label,
      count: item.count,
      color: item.isBillable ? BILLABLE_COLOR : NON_BILLABLE_COLOR,
    })),
    125,
  );

const buildBillableOptions = (split: EngagementMetricsBillableSplit) =>
  buildDonutOptions([
    { name: 'Billable', value: split.billableCount, color: BILLABLE_COLOR },
    { name: 'Non-billable', value: split.nonBillableCount, color: NON_BILLABLE_COLOR },
  ]);

export const EngagementTypeAndBillableCharts = ({
  byType,
  billableSplit,
}: EngagementTypeAndBillableChartsProps) => {
  const hasTypeData = byType.some((item) => item.count > 0);
  const hasBillableData = billableSplit.billableCount + billableSplit.nonBillableCount > 0;

  return (
    <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-2">
      <ChartContainer
        title="Engagements by Type"
        subtitle="Qualifying signals colored by billable status"
      >
        {hasTypeData ? (
          <ReactEChartsCore
            echarts={echarts}
            option={buildTypeOptions(byType)}
            style={{ height: 300 }}
            notMerge
            lazyUpdate
          />
        ) : (
          <EmptyState message="No engagement type data available." />
        )}
      </ChartContainer>

      <ChartContainer title="Billable Split" subtitle="Billable vs non-billable engagements">
        {hasBillableData ? (
          <ReactEChartsCore
            echarts={echarts}
            option={buildBillableOptions(billableSplit)}
            style={{ height: 300 }}
            notMerge
            lazyUpdate
          />
        ) : (
          <EmptyState message="No billable split data available." />
        )}
      </ChartContainer>
    </div>
  );
};
